/**
 * Whole-dollar currency formatting (NFR-12). Never show decimals in the UI.
 */

export function formatMoney(
  amount: number | string | null | undefined,
  currency = 'USD',
  locale = 'en-US',
): string {
  if (amount == null || amount === '') return '—'
  const numeric = typeof amount === 'string' ? Number(amount) : amount
  if (!Number.isFinite(numeric)) return '—'
  const whole = Math.round(numeric)
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(whole)
  } catch {
    return `${currency} ${whole}`
  }
}

/** Whole-dollar amount without currency symbol (for tooltips / titles). */
export function formatMoneyAmount(
  amount: number | string | null | undefined,
  locale = 'en-US',
): string {
  if (amount == null || amount === '') return '—'
  const numeric = typeof amount === 'string' ? Number(amount) : amount
  if (!Number.isFinite(numeric)) return '—'
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Math.round(numeric))
}

/**
 * Compact whole-dollar display for dense month strips (e.g. $1.2M).
 * Full amount remains available via formatMoney / tooltips.
 */
export function formatMoneyCompact(
  amount: number | string | null | undefined,
  currency = 'USD',
  locale = 'en-US',
): string {
  if (amount == null || amount === '') return '—'
  const numeric = typeof amount === 'string' ? Number(amount) : amount
  if (!Number.isFinite(numeric)) return '—'
  const whole = Math.round(numeric)
  const abs = Math.abs(whole)
  try {
    if (abs < 10_000) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }).format(whole)
    }
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: abs >= 1_000_000 ? 1 : 0,
      minimumFractionDigits: 0,
    }).format(whole)
  } catch {
    return formatMoney(amount, currency, locale)
  }
}
